import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { dashboardApi } from '../../lib/api';
import {
  Bell,
  MessageSquare,
  HelpCircle,
  Settings,
  Users,
  GraduationCap,
  BookOpen,
  Plus,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  X,
  Search,
  Check,
  Clock,
  TrendingUp,
  Video,
  Loader2,
} from 'lucide-react';

// Types
interface DashboardStats {
  students: { total: number; new_this_month: number };
  teachers: { total: number; new_this_month: number };
  courses: { total: number; active: number; online: number };
  meetings: { total: number; upcoming: number; live: number };
  attendance: { monthly: number; daily: number };
}

interface CalendarEvent {
  id: string;
  title: string;
  course: string | null;
  start: string;
  end: string;
  status: string;
  type: string;
}

interface WeeklySchedule {
  day: string;
  events: Array<{ id: string; title: string; time: string; status: string }>;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  time: string;
  read?: boolean;
}

interface FinanceData {
  chart_data: Array<{ label: string; income: number; expenses: number }>;
  summary: { total_income: number; total_expenses: number; net: number };
}

// Color map for event types
const eventColors: Record<string, string> = {
  meeting: 'bg-teal-500',
  course: 'bg-blue-500',
  scheduled: 'bg-purple-500',
  in_progress: 'bg-green-500',
  completed: 'bg-gray-400',
};

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, currentSchool } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState<'all' | 'unread'>('all');
  const [currentDate] = useState(new Date());

  // API data states
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [financeData, setFinanceData] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, calendarRes, notificationsRes, financeRes] = await Promise.all([
          dashboardApi.getStats(),
          dashboardApi.getCalendar(),
          dashboardApi.getNotifications(),
          dashboardApi.getFinance(),
        ]);

        setStats(statsRes.stats);
        setCalendarEvents(calendarRes.today);
        setWeeklySchedule(calendarRes.weekly);
        setNotifications(
          notificationsRes.notifications.map((n: any) => ({ ...n, read: false }))
        );
        setFinanceData(financeRes);
      } catch (err: any) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data. Using sample data.');
        // Set fallback mock data
        setStats({
          students: { total: 156, new_this_month: 12 },
          teachers: { total: 24, new_this_month: 2 },
          courses: { total: 18, active: 12, online: 8 },
          meetings: { total: 45, upcoming: 5, live: 2 },
          attendance: { monthly: 95, daily: 92 },
        });
        setCalendarEvents([
          { id: '1', title: 'Staff Meeting: Jungle Class', course: null, start: '2024-01-15T09:00:00', end: '2024-01-15T10:00:00', status: 'scheduled', type: 'meeting' },
          { id: '2', title: 'Mathematics - Grade 10', course: 'Math 10A', start: '2024-01-15T10:30:00', end: '2024-01-15T12:00:00', status: 'scheduled', type: 'course' },
          { id: '3', title: 'Physics Lab Session', course: 'Physics 11', start: '2024-01-15T14:00:00', end: '2024-01-15T15:30:00', status: 'scheduled', type: 'course' },
        ]);
        setWeeklySchedule([
          { day: 'Mon', events: [{ id: '1', title: 'Math 10A', time: '09:00', status: 'scheduled' }] },
          { day: 'Tue', events: [{ id: '2', title: 'English 10A', time: '10:00', status: 'scheduled' }] },
          { day: 'Wed', events: [{ id: '3', title: 'Physics 11', time: '09:00', status: 'scheduled' }] },
          { day: 'Thu', events: [{ id: '4', title: 'Math 10A', time: '11:00', status: 'scheduled' }] },
          { day: 'Fri', events: [{ id: '5', title: 'English 10A', time: '10:00', status: 'scheduled' }] },
        ]);
        setFinanceData({
          chart_data: [
            { label: 'Jul', income: 4500, expenses: 2100 },
            { label: 'Aug', income: 5200, expenses: 2400 },
            { label: 'Sep', income: 4800, expenses: 1900 },
            { label: 'Oct', income: 6100, expenses: 2800 },
            { label: 'Nov', income: 5500, expenses: 2200 },
            { label: 'Dec', income: 7200, expenses: 3100 },
          ],
          summary: { total_income: 33300, total_expenses: 14500, net: 18800 },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications =
    notificationFilter === 'all'
      ? notifications
      : notifications.filter((n) => !n.read);

  const markAsRead = (id: string) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatEventTime = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} - ${endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* My Students */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/students')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/students/new'); }}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">My Students</h3>
          <p className="text-2xl font-bold text-gray-900">{stats?.students.total ?? 0}</p>
          {stats?.students.new_this_month ? (
            <p className="text-xs text-green-600 mt-1">
              +{stats.students.new_this_month} this month
            </p>
          ) : null}
        </div>

        {/* My Teachers */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/teachers')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/teachers/new'); }}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">My Teachers</h3>
          <p className="text-2xl font-bold text-gray-900">{stats?.teachers.total ?? 0}</p>
          {stats?.teachers.new_this_month ? (
            <p className="text-xs text-green-600 mt-1">
              +{stats.teachers.new_this_month} this month
            </p>
          ) : null}
        </div>

        {/* Courses */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/courses')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/courses/new'); }}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Courses</h3>
          <p className="text-2xl font-bold text-gray-900">{stats?.courses.total ?? 0}</p>
        </div>

        {/* Quick Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/meetings')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Quick Stats</h3>
          <div className="flex items-center gap-4 text-sm">
            <span>
              <strong>{stats?.courses.online ?? 0}</strong> Online
            </span>
            <span>
              <strong>{stats?.meetings.upcoming ?? 0}</strong> Upcoming
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Calendar & Events */}
        <div className="lg:col-span-2 space-y-6">
          {/* My Calendar Widget */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">My Calendar</h2>
              <div className="flex items-center gap-2">
                <button className="p-1 hover:bg-gray-100 rounded">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium">
                  {currentDate.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Mini Calendar */}
            <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
              <div className="text-center bg-teal-600 text-white rounded-xl p-3 min-w-[70px]">
                <div className="text-3xl font-bold">{currentDate.getDate()}</div>
                <div className="text-xs opacity-80">
                  {currentDate.toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">{formatDate(currentDate)}</p>
                <p className="font-medium text-gray-900">
                  {calendarEvents.length} events today
                </p>
              </div>
              <button
                onClick={() => navigate('/calendar')}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                See All
              </button>
            </div>

            {/* Today's Events */}
            <div className="space-y-3">
              {calendarEvents.length > 0 ? (
                calendarEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div
                      className={`w-1 h-12 rounded-full ${
                        eventColors[event.status] || eventColors[event.type] || 'bg-teal-500'
                      }`}
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {event.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatEventTime(event.start, event.end)}
                      </div>
                    </div>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No events scheduled for today
                </div>
              )}
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">School Schedule</h2>
              <button
                onClick={() => navigate('/schedule')}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                View Full Schedule
              </button>
            </div>

            <div className="grid grid-cols-5 gap-3">
              {weeklySchedule.map((day) => (
                <div key={day.day} className="text-center">
                  <div className="text-sm font-medium text-gray-500 mb-2">
                    {day.day}
                  </div>
                  <div className="space-y-2 min-h-[120px]">
                    {day.events.map((event) => (
                      <div
                        key={event.id}
                        className={`${
                          eventColors[event.status] || 'bg-teal-500'
                        } text-white text-xs p-2 rounded-lg cursor-pointer hover:opacity-90`}
                      >
                        <div className="font-medium truncate">{event.title}</div>
                        <div className="opacity-80">{event.time}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Attendance & Finance */}
        <div className="space-y-6">
          {/* Attendance Summary */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Attendance</h2>
              <button
                onClick={() => navigate('/attendance')}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                Details
              </button>
            </div>

            <div className="flex items-center justify-center gap-8">
              {/* Monthly Attendance */}
              <div className="text-center">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="35"
                      stroke="#e5e7eb"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="35"
                      stroke="#14b8a6"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${(stats?.attendance.monthly ?? 0) * 2.2} ${100 * 2.2}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-900">
                      {stats?.attendance.monthly ?? 0}%
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Monthly</p>
              </div>

              {/* Daily Attendance */}
              <div className="text-center">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="35"
                      stroke="#e5e7eb"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="35"
                      stroke="#3b82f6"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${(stats?.attendance.daily ?? 0) * 2.2} ${100 * 2.2}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-900">
                      {stats?.attendance.daily ?? 0}%
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Daily</p>
              </div>
            </div>
          </div>

          {/* Finance Overview */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Finance</h2>
              <button
                onClick={() => navigate('/finance')}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                See All
              </button>
            </div>

            {/* Simple Bar Chart */}
            <div className="flex items-end justify-between h-32 gap-2">
              {financeData?.chart_data.map((item) => (
                <div key={item.label} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center gap-1 h-24 justify-end">
                    <div
                      className="w-full bg-teal-500 rounded-t"
                      style={{ height: `${(item.income / 8000) * 100}%` }}
                    />
                    <div
                      className="w-full bg-red-400 rounded-t"
                      style={{ height: `${(item.expenses / 8000) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-teal-500 rounded" />
                <span className="text-xs text-gray-600">Income</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-400 rounded" />
                <span className="text-xs text-gray-600">Expenses</span>
              </div>
            </div>
          </div>

          {/* User Profile Card */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-5 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold">
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </div>
              <div>
                <h3 className="font-semibold">
                  {user?.full_name || 'Admin User'}
                </h3>
                <p className="text-sm opacity-80">
                  {currentSchool?.role || 'Administrator'}
                </p>
              </div>
              <div className="ml-auto">
                <div className="w-3 h-3 bg-green-400 rounded-full" title="Online" />
              </div>
            </div>
            <div className="text-sm opacity-80">
              <p>{currentSchool?.name || 'VIScool School'}</p>
              <p>{user?.email || 'admin@viscool.com'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Panel */}
      {showNotifications && (
        <NotificationsPanel
          notifications={filteredNotifications}
          filter={notificationFilter}
          onFilterChange={setNotificationFilter}
          onClose={() => setShowNotifications(false)}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
        />
      )}
    </div>
  );
}

interface NotificationsPanelProps {
  notifications: Notification[];
  filter: 'all' | 'unread';
  onFilterChange: (filter: 'all' | 'unread') => void;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

function NotificationsPanel({
  notifications,
  filter,
  onFilterChange,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationsPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const filteredNotifications = notifications.filter((n) => {
    const matchesSearch =
      n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || n.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Notifications Center
            </h2>
            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => onFilterChange('all')}
              className={`text-sm font-medium ${
                filter === 'all'
                  ? 'text-teal-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => onFilterChange('unread')}
              className={`text-sm font-medium ${
                filter === 'unread'
                  ? 'text-teal-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Unread
            </button>
            <div className="ml-auto">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              >
                <option value="all">All Types</option>
                <option value="course">Courses</option>
                <option value="teacher">Teachers</option>
                <option value="student">Students</option>
                <option value="payment">Finance</option>
                <option value="system">System</option>
              </select>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Mark All as Read */}
        <div className="px-4 py-2 border-b border-gray-100">
          <button
            onClick={onMarkAllAsRead}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            Mark all as read
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications.length > 0 ? (
            <div className="p-4 space-y-2">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`relative p-3 rounded-lg border transition-colors cursor-pointer ${
                    notification.read
                      ? 'bg-white border-gray-100 hover:bg-gray-50'
                      : 'bg-teal-50 border-teal-100 hover:bg-teal-100'
                  }`}
                  onClick={() => onMarkAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                        notification.type === 'payment'
                          ? 'bg-green-100 text-green-600'
                          : notification.type === 'system'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-teal-100 text-teal-600'
                      }`}
                    >
                      {notification.title.charAt(0)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-teal-500 rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(notification.time).toLocaleString()}
                      </p>
                    </div>

                    {/* Options Menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenId(
                            menuOpenId === notification.id ? null : notification.id
                          );
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {menuOpenId === notification.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkAsRead(notification.id);
                              setMenuOpenId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Check className="w-4 h-4" />
                            Mark as Read
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Mute
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Bell className="w-12 h-12 mb-2 opacity-50" />
              <p>No notifications</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
